import clsx from 'clsx';
import { type ElementType, forwardRef, type ReactElement } from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { type DataTestId } from '../../core/types/data-props.js';
import { type MaskingProps } from '../../core/types/masking-props.js';
import { type PolymorphicComponentProps } from '../../core/types/polymorph.js';
import { type StylingProps } from '../../core/types/styling-props.js';
import { type WithChildren } from '../../core/types/with-children.js';
import {
  getGridStyles,
  type GridStyleProps,
} from '../../styles/getGridStyles.js';

const COMPONENT_NAME = 'Grid';

/**
 * Defines grid own props.
 * @public
 */
export type GridOwnProps = WithChildren &
  StylingProps &
  GridStyleProps &
  DataTestId &
  MaskingProps &
  BehaviorTrackingProps;

/**
 * Merge own props with others inherited from the underlying element type.
 * @public
 */
export type GridProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  GridOwnProps
>;

/**
 * The `Grid` component can be used to layout its children with the help of CSS Grid.
 * The component can be customized with CSS grid props.
 * @public
 */
export const Grid: <E extends ElementType = 'div'>(
  props: GridProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: GridProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      children,
      id,
      as = 'div',
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'data-dt-component': dataDtComponent = COMPONENT_NAME,
      ...remainingProps
    } = props;

    const [gridClassNames, gridStyles, nonGridStyleProps] = getGridStyles(
      remainingProps,
      { gap: 8 },
    );

    const [behavioralTrackingProps, restNonGridProps] =
      useBehavioralTrackingProps(nonGridStyleProps);

    const Component = as ?? 'div';

    return (
      <Component
        ref={forwardedRef}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        id={id}
        className={clsx(gridClassNames, consumerClassName)}
        style={{
          ...gridStyles,
          ...consumerStyle,
        }}
        {...restNonGridProps}
        {...behavioralTrackingProps}
        data-dt-component={dataDtComponent}
      >
        {children}
      </Component>
    );
  },
);
